import { useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { useMedicinesCatalog } from "../../medicines/context/MedicinesCatalogContext";
import PublicCartSidebar from "../components/PublicCartSidebar";
import PublicFooter from "../components/PublicFooter";
import PublicNavbar from "../components/PublicNavbar";
import {
  CATEGORIES,
  getCategoryByKey,
  getCategoryIcon,
} from "../constants/catalog";
import { CartItem, PublicDeliveryInfo } from "../types/store";
import { usePageMeta } from "../../../shared/seo/PageMeta";

type PublicCategoryPageProps = {
  deliveryInfo: PublicDeliveryInfo;
  cartItems: CartItem[];
  cartUnits: number;
  isCartOpen: boolean;
  onOpenCart: () => void;
  onCloseCart: () => void;
  onAddToCart: (payload: {
    id: number;
    name: string;
    unitPrice: number;
    quantity: number;
    icon?: string;
    image?: string;
  }) => void;
  onRemoveCartItem: (itemId: string) => void;
  onDeliveryChange: (delivery: PublicDeliveryInfo) => void;
  onLogout: () => void;
  onCheckout: () => void;
};

const ITEMS_PER_PAGE = 10;

export default function PublicCategoryPage({
  deliveryInfo,
  cartItems,
  cartUnits,
  isCartOpen,
  onOpenCart,
  onCloseCart,
  onAddToCart,
  onRemoveCartItem,
  onDeliveryChange,
  onLogout,
  onCheckout,
}: PublicCategoryPageProps) {
  const { medicines } = useMedicinesCatalog();
  const { categoria } = useParams();
  const category = getCategoryByKey(categoria || "");
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [productQuantities, setProductQuantities] = useState<
    Record<number, number>
  >({});

  const filteredProducts = useMemo(() => {
    if (!category) {
      return [];
    }
    const query = searchQuery.trim().toLowerCase();
    return medicines
      .filter((medicine) => medicine.categoryKey === category.key)
      .filter((medicine) =>
        query ? medicine.name.toLowerCase().includes(query) : true,
      );
  }, [medicines, category, searchQuery]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredProducts.length / ITEMS_PER_PAGE),
  );
  const safePage = Math.min(page, totalPages);

  const paginatedProducts = useMemo(() => {
    const start = (safePage - 1) * ITEMS_PER_PAGE;
    return filteredProducts.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredProducts, safePage]);

  const changeProductQuantity = (productId: number, delta: number) => {
    setProductQuantities((prev) => {
      const current = prev[productId] ?? 1;
      const next = Math.max(1, current + delta);
      return { ...prev, [productId]: next };
    });
  };

  usePageMeta({
    title: category?.name ?? 'Categoria de productos',
    description: category
      ? `Compra productos de ${category.name} en Boticlick con entrega a domicilio.`
      : 'Compra productos por categoria en Boticlick con entrega a domicilio.',
    path: category ? `/categoria/${category.key}` : '/productos',
    keywords: category ? `${category.name}, farmacia, boticlick, productos` : 'farmacia, boticlick, productos',
  });

  if (!category) {
    return <Navigate to="/productos" replace />;
  }

  return (
    <main className="public-page-shell">
      <PublicNavbar
        deliveryInfo={deliveryInfo}
        cartUnits={cartUnits}
        onOpenCart={onOpenCart}
        onDeliveryChange={onDeliveryChange}
      />

      <section className="card content-card public-home-card">
        <header className="public-products-header">
          <p className="subtitle category-location">
            <Link
              to="/productos"
              className="category-back-link"
              aria-label="Volver al inicio"
            >
              ←
            </Link>
            Estas en categoria <strong>{category.name}</strong>
          </p>
        </header>

        <div className="category-chips-wrap">
          {CATEGORIES.map((categoryItem) => (
            <Link
              key={categoryItem.key}
              to={`/categoria/${categoryItem.key}`}
              className={`category-chip ${categoryItem.key === category.key ? "is-active" : ""}`}
            >
              <span aria-hidden="true">{categoryItem.icon}</span>
              {categoryItem.name}
            </Link>
          ))}
        </div>

        <div className="public-search-wrap">
          <input
            type="text"
            placeholder="¿Qué producto estás buscando hoy?"
            value={searchQuery}
            onChange={(event) => {
              setSearchQuery(event.target.value);
              setPage(1);
            }}
          />
        </div>

        <section className="public-products-section">
          <header className="table-header public-products-header">
            <h3 className="section-title">Productos de {category.name}</h3>
            <span className="subtitle">
              Mostrando {paginatedProducts.length} de {filteredProducts.length}
            </span>
          </header>

          <div className="public-products-grid">
            {paginatedProducts.map((product) => (
              <article key={product.id} className="public-product-card">
                <div className="public-product-image">
                  {product.image ? (
                    <img src={product.image} alt={product.name} loading="lazy" />
                  ) : (
                    <span>{getCategoryIcon(product.categoryKey)}</span>
                  )}
                </div>
                <div className="public-product-info">
                  <p className="public-product-name">{product.name}</p>
                  {product.concentration ? (
                    <p className="public-product-concentration">{product.concentration}</p>
                  ) : null}
                  {product.presentation ? (
                    <p className="public-product-presentation">{product.presentation}</p>
                  ) : null}
                </div>
                <p className="public-product-price">
                  S/ {product.unitPrice.toFixed(2)}
                </p>
                <div className="product-qty-control">
                  <button
                    type="button"
                    className="qty-btn"
                    onClick={() => changeProductQuantity(product.id, -1)}
                  >
                    -
                  </button>
                  <span>{productQuantities[product.id] ?? 1}</span>
                  <button
                    type="button"
                    className="qty-btn"
                    onClick={() => changeProductQuantity(product.id, 1)}
                  >
                    +
                  </button>
                </div>
                <button
                  type="button"
                  className="add-to-cart-btn"
                  onClick={() =>
                    onAddToCart({
                      id: product.id,
                      name: product.name,
                      unitPrice: product.unitPrice,
                      quantity: productQuantities[product.id] ?? 1,
                      icon: getCategoryIcon(product.categoryKey),
                      image: product.image,
                    })
                  }
                >
                  Agregar al carrito
                </button>
              </article>
            ))}
          </div>

          {paginatedProducts.length === 0 ? (
            <p className="subtitle">
              No hay productos para mostrar en esta categoria.
            </p>
          ) : null}

          <div className="pagination">
            <button
              type="button"
              onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
              disabled={safePage === 1}
            >
              Anterior
            </button>
            <span>
              Pagina {safePage} de {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={safePage === totalPages}
            >
              Siguiente
            </button>
          </div>
        </section>
      </section>

      <PublicFooter onLogout={onLogout} />

      <PublicCartSidebar
        isOpen={isCartOpen}
        cartItems={cartItems}
        onClose={onCloseCart}
        onRemoveItem={onRemoveCartItem}
        onCheckout={onCheckout}
      />
    </main>
  );
}
